import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { useApi, errMsg, fmtTime } from "../lib/api";

const KINDS = { event: "Events", training: "Training", partner: "Partners", blog: "Blog posts" };
const HINTS = {
  event: { summary: "Type, e.g. Community launch", location: "Venue, town", category: "Time, e.g. 10:00" },
  training: { summary: "Duration or details", location: "Venue or online", category: "Level (optional)" },
  partner: { summary: "Short description (optional)", location: "Country (optional)", category: "Partner type (optional)" },
  blog: { summary: "Short summary", location: "Read time, e.g. 4 min", category: "Category, e.g. Safety" },
};
const blank = { title: "", summary: "", date: "", location: "", url: "", category: "", published: true };

function ItemForm({ kind, onSaved }) {
  const api = useApi();
  const [f, setF] = useState(blank);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fileKey, setFileKey] = useState(0);
  const h = HINTS[kind];
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/site/${kind}`, { ...f, date: f.date || null });
      if (file) { const fd = new FormData(); fd.append("file", file); await api.post(`/admin/site/item/${r.data.id}/image`, fd); }
      setF(blank); setFile(null); setFileKey((k) => k + 1); toast.success("Added – it's live on the About page"); onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="panel p-5 grid md:grid-cols-2 gap-3" data-testid={`site-form-${kind}`}>
      <Input data-testid="site-title-input" placeholder={kind === "partner" ? "Partner name" : "Title"} value={f.title} onChange={set("title")} />
      <Input data-testid="site-date-input" type="date" value={f.date} onChange={set("date")} />
      <Input data-testid="site-location-input" placeholder={h.location} value={f.location} onChange={set("location")} />
      <Input data-testid="site-category-input" placeholder={h.category} value={f.category} onChange={set("category")} />
      <Textarea data-testid="site-summary-input" className="md:col-span-2" rows={2} placeholder={h.summary} value={f.summary} onChange={set("summary")} />
      <Input data-testid="site-url-input" placeholder="Link (registration, website or article URL)" value={f.url} onChange={set("url")} />
      <label className="text-sm flex items-center gap-2"><span className="text-slate-400">{kind === "partner" ? "Logo" : "Image"}</span>
        <input key={fileKey} data-testid="site-image-input" type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0] || null)} className="text-xs" /></label>
      <Button data-testid="site-add-btn" disabled={busy || !f.title.trim()} onClick={save} className="md:col-span-2 bg-red-600 hover:bg-red-500 text-white">Add {KINDS[kind].toLowerCase().replace(/s$/, "")}</Button>
    </div>
  );
}

export default function SiteManager() {
  const api = useApi();
  const [kind, setKind] = useState("event");
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/admin/site").then((r) => setItems(r.data)), [api]);
  useEffect(() => { load(); }, [load]);
  const toggle = async (i) => { await api.put(`/admin/site/item/${i.id}`, { ...i, published: !i.published }); load(); };
  const del = async (i) => { if (window.confirm(`Delete "${i.title}"?`)) { await api.delete(`/admin/site/item/${i.id}`); load(); } };
  const shown = items.filter((i) => i.kind === kind);
  return (
    <div data-testid="site-manager">
      <div className="flex flex-wrap gap-2 mb-4">{Object.entries(KINDS).map(([k, l]) => (
        <button key={k} data-testid={`site-kind-${k}`} onClick={() => setKind(k)} className={`text-xs px-3 py-1.5 rounded-full border ${kind === k ? "border-red-500 text-white" : "border-slate-700 text-slate-400"}`}>
          {l} ({items.filter((i) => i.kind === k).length})</button>))}</div>
      <ItemForm key={kind} kind={kind} onSaved={load} />
      <div className="mt-4 space-y-2">{shown.length === 0 ? <div className="text-sm text-slate-500" data-testid="site-empty">Nothing yet – the About page shows placeholders until you add some.</div> : shown.map((i) => (
        <div key={i.id} className="panel p-3 flex items-center gap-3 text-sm" data-testid={`site-item-${i.id}`}>
          <div className="flex-1 min-w-0"><div className="font-semibold truncate">{i.title}</div><div className="text-xs text-slate-400 truncate">{[i.date && fmtTime(i.date).split(",")[0], i.location, i.summary].filter(Boolean).join(" · ")}{i.has_image ? " · image" : ""}</div></div>
          <label className="flex items-center gap-2 text-xs text-slate-300">Live<Switch data-testid={`site-publish-${i.id}`} checked={i.published} onCheckedChange={() => toggle(i)} /></label>
          <Button size="sm" variant="ghost" className="text-red-300" data-testid={`site-delete-${i.id}`} onClick={() => del(i)}>Delete</Button>
        </div>))}</div>
    </div>
  );
}
